// pages/admin/AdminPaymentMethods.jsx
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../utils/auth';
import api from '../../utils/api';
import { Plus, Edit2, Trash2, Eye, EyeOff, Banknote, CreditCard, ScrollText, Landmark, Gift, Phone } from 'lucide-react';
import toast from 'react-hot-toast';
import ConfirmModal from '../../components/ConfirmModal';

// Icônes canoniques — mêmes clés que le ICON_MAP utilisé côté encaissement
// (BookingDetail.jsx), pour un rendu cohérent entre l'admin et la caisse.
// Les anciennes clés ('banknotes', 'arrow-right', 'document'...) restent
// acceptées à l'affichage (voir ICON_RENDER) pour ne pas casser les moyens
// de paiement existants, mais un nouveau moyen créé ici utilise toujours
// une clé canonique.
const ICON_OPTIONS = [
  { value:'cash',        label:'Espèces',       Icon:Banknote },
  { value:'card',        label:'Carte bancaire', Icon:CreditCard },
  { value:'cheque',      label:'Chèque',         Icon:ScrollText },
  { value:'transfer',    label:'Virement',       Icon:Landmark },
  { value:'gift',        label:'Bon cadeau',     Icon:Gift },
  { value:'cb_distance', label:'CB à distance',  Icon:Phone },
];

const ICON_RENDER = {
  cash:Banknote, banknotes:Banknote,
  card:CreditCard, 'credit-card':CreditCard, stripe:CreditCard,
  cheque:ScrollText, document:ScrollText,
  transfer:Landmark, 'arrow-right':Landmark,
  gift:Gift, cb_distance:Phone,
};

function renderIcon(icon, size = 20) {
  const Icon = ICON_RENDER[icon] || CreditCard;
  return <Icon size={size} />;
}

function MethodModal({ method, onClose, onSaved }) {
  const [form, setForm] = useState({
    name:       method?.name || '',
    icon:       method?.icon || 'cash',
    isActive:   method?.is_active !== false,
    isPos:      method?.is_pos !== false,
    isOnline:   method?.is_online || false,
    isManual:   method?.is_manual !== false,
    isCash:     method?.is_cash || false,
  });
  const [saving, setSaving] = useState(false);

  const submit = async e => {
    e.preventDefault();
    setSaving(true);
    try {
      if (method) {
        await api.put('/payments/methods/' + method.id, form);
        toast.success('Moyen de paiement mis à jour');
      } else {
        await api.post('/payments/methods', form);
        toast.success('Moyen de paiement créé');
      }
      onSaved();
      onClose();
    } catch(e) { toast.error(e.response?.data?.error || 'Erreur'); }
    finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal" style={{ maxWidth:460 }}>
        <h3 className="modal-title">{method ? 'Modifier' : 'Nouveau'} moyen de paiement</h3>
        <form onSubmit={submit}>
          <div className="form-group">
            <label className="form-label">Nom *</label>
            <input className="input" required value={form.name}
              onChange={e=>setForm(p=>({...p,name:e.target.value}))}
              placeholder="Ex: Espèces, Carte bancaire…"/>
          </div>

          <div className="form-group">
            <label className="form-label">Icône</label>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:6, marginTop:4 }}>
              {ICON_OPTIONS.map(opt => (
                <button key={opt.value} type="button"
                  onClick={() => setForm(p=>({...p,icon:opt.value}))}
                  style={{ padding:'10px 6px', borderRadius:10, border:`1px solid ${form.icon===opt.value?'var(--accent)':'var(--border)'}`,
                    background:form.icon===opt.value?'rgba(124,92,252,0.12)':'var(--bg-elevated)',
                    cursor:'pointer', textAlign:'center', fontFamily:'inherit',
                    display:'flex', flexDirection:'column', alignItems:'center', gap:4 }}>
                  <opt.Icon size={18} color={form.icon===opt.value?'var(--accent-light)':'var(--text-2)'} />
                  <div style={{ color:form.icon===opt.value?'var(--accent-light)':'var(--text-3)', fontSize:10 }}>
                    {opt.label}
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div style={{ display:'flex', flexDirection:'column', gap:10, marginTop:16 }}>
            {[
              ['isActive', 'Actif (visible partout)'],
              ['isPos',    'Disponible en mode caisse (POS)'],
              ['isOnline', 'Disponible en réservation en ligne'],
              ['isManual', 'Disponible pour les réservations manuelles'],
            ].map(([key, label]) => (
              <label key={key} style={{ display:'flex', alignItems:'center', gap:10,
                cursor:'pointer', fontSize:14 }}>
                <input type="checkbox" checked={form[key]}
                  onChange={e=>setForm(p=>({...p,[key]:e.target.checked}))}/>
                {label}
              </label>
            ))}
            <label style={{ display:'flex', alignItems:'center', gap:10, cursor:'pointer', fontSize:14,
              padding:'8px 10px', borderRadius:8, background:'var(--bg-elevated)' }}>
              <input type="checkbox" checked={form.isCash}
                onChange={e=>setForm(p=>({...p,isCash:e.target.checked}))}/>
              <div>
                <div>Compter comme espèces (comptage de caisse)</div>
                <div style={{ fontSize:11, color:'var(--text-3)' }}>
                  Utilisé pour le total "espèces du jour" dans app/caisse. Ne coche ça que sur UN SEUL
                  moyen de paiement — si tu as un doublon "Espèces", fusionne-le plutôt que de cocher les deux.
                </div>
              </div>
            </label>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Annuler</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? <span className="spinner" style={{width:14,height:14}}/> : 'Enregistrer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Modale de fusion, affichée automatiquement quand une suppression échoue
// parce que le moyen de paiement a des paiements existants (erreur 409
// HAS_PAYMENTS) — plutôt que de laisser l'admin bloqué sur une erreur
// serveur opaque comme avant.
function MergeModal({ method, methods, onClose, onMerged }) {
  const [targetId, setTargetId] = useState('');
  const [merging, setMerging] = useState(false);
  const candidates = methods.filter(m => m.id !== method.id);

  const doMerge = async () => {
    if (!targetId) return toast.error('Choisis le moyen de paiement de destination');
    setMerging(true);
    try {
      await api.delete(`/payments/methods/${method.id}?mergeInto=${targetId}`);
      toast.success('Fusionné et supprimé');
      onMerged();
      onClose();
    } catch(e) { toast.error(e.response?.data?.error || 'Erreur'); }
    finally { setMerging(false); }
  };

  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal" style={{ maxWidth:440 }}>
        <h3 className="modal-title">Fusionner "{method.name}"</h3>
        <p style={{ fontSize:13, color:'var(--text-3)', marginBottom:14 }}>
          Ce moyen de paiement est utilisé par des paiements existants — il ne peut pas être
          supprimé directement. Choisis un autre moyen de paiement : tous les paiements de
          "{method.name}" lui seront réattribués, puis "{method.name}" sera supprimé.
        </p>
        <div className="form-group">
          <label className="form-label">Fusionner vers</label>
          <select className="input" value={targetId} onChange={e => setTargetId(e.target.value)}>
            <option value="">— Choisir —</option>
            {candidates.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose}>Annuler</button>
          <button type="button" className="btn btn-danger" onClick={doMerge} disabled={merging || !targetId}>
            {merging ? <span className="spinner" style={{width:14,height:14}}/> : 'Fusionner et supprimer'}
          </button>
        </div>
      </div>
    </div>
  );
}

// `embedded` : rendu comme onglet interne (page Tarification).
export default function AdminPaymentMethods({ embedded = false }) {
  const { can } = useAuth();
  const [methods, setMethods] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showNew, setNew]     = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [mergeTarget, setMergeTarget] = useState(null);
  const [removing, setRemoving] = useState(false);

  const load = useCallback(() => {
    api.get('/payments/methods/all').then(r => setMethods(r.data.methods || [])).catch(() => {
      api.get('/payments/methods').then(r => setMethods(r.data.methods || []));
    });
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (method) => {
    try {
      await api.put('/payments/methods/' + method.id, { ...method, isActive: !method.is_active });
      toast.success(method.is_active ? 'Désactivé' : 'Activé');
      load();
    } catch { toast.error('Erreur'); }
  };

  const remove = async () => {
    setRemoving(true);
    try {
      await api.delete('/payments/methods/' + confirmRemove.id);
      toast.success('Supprimé');
      setConfirmRemove(null);
      load();
    } catch(e) {
      if (e.response?.data?.code === 'HAS_PAYMENTS') {
        // Basculer directement vers la fusion plutôt que d'afficher une
        // erreur sans solution.
        setMergeTarget(confirmRemove);
        setConfirmRemove(null);
      } else {
        toast.error(e.response?.data?.error || 'Erreur');
      }
    }
    finally { setRemoving(false); }
  };

  if (!can('payments.manage')) return (
    <div className="page-body"><div className="empty-state card"><p>Accès non autorisé</p></div></div>
  );

  const cashMethods = methods.filter(m => m.is_cash);

  const newMethodBtn = (
    <button className="btn btn-primary" onClick={() => setNew(true)}>
      <Plus size={14}/> Nouveau
    </button>
  );

  return (
    <>
      {embedded ? (
        <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:14 }}>{newMethodBtn}</div>
      ) : (
        <div className="page-header">
          <div>
            <h2>Moyens de paiement</h2>
            <p>Gérez les modes de règlement disponibles</p>
          </div>
          {newMethodBtn}
        </div>
      )}

      <div className={embedded ? undefined : 'page-body'}>
        {cashMethods.length > 1 && (
          <div style={{ marginBottom:14, padding:'10px 14px', borderRadius:8,
            background:'rgba(245,166,35,0.08)', border:'1px solid var(--amber)',
            fontSize:12, color:'var(--amber)' }}>
            ⚠️ {cashMethods.length} moyens de paiement sont marqués "espèces" ({cashMethods.map(m=>m.name).join(', ')}) —
            le total "espèces du jour" dans app/caisse va les additionner tous. Si ce sont des doublons, fusionne-les.
          </div>
        )}
        <div className="card" style={{ padding:0, overflow:'hidden' }}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead>
              <tr style={{ borderBottom:'1px solid var(--border)' }}>
                <th style={{ padding:'12px 20px', textAlign:'left', fontSize:12,
                  color:'var(--text-3)', fontWeight:600 }}>Moyen de paiement</th>
                <th style={{ padding:'12px 16px', textAlign:'center', fontSize:12,
                  color:'var(--text-3)', fontWeight:600 }}>POS</th>
                <th style={{ padding:'12px 16px', textAlign:'center', fontSize:12,
                  color:'var(--text-3)', fontWeight:600 }}>En ligne</th>
                <th style={{ padding:'12px 16px', textAlign:'center', fontSize:12,
                  color:'var(--text-3)', fontWeight:600 }}>Manuel</th>
                <th style={{ padding:'12px 16px', textAlign:'center', fontSize:12,
                  color:'var(--text-3)', fontWeight:600 }}>Espèces</th>
                <th style={{ padding:'12px 16px', textAlign:'center', fontSize:12,
                  color:'var(--text-3)', fontWeight:600 }}>Statut</th>
                <th style={{ padding:'12px 20px', textAlign:'right', fontSize:12,
                  color:'var(--text-3)', fontWeight:600 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {methods.map(m => (
                <tr key={m.id} style={{ borderBottom:'1px solid var(--border)',
                  opacity: m.is_active ? 1 : 0.5 }}>
                  <td style={{ padding:'12px 20px' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                      <span style={{ color:'var(--text-2)' }}>{renderIcon(m.icon)}</span>
                      <span style={{ fontWeight:600 }}>{m.name}</span>
                    </div>
                  </td>
                  <td style={{ textAlign:'center', padding:'12px 16px' }}>
                    {m.is_pos !== false ? '✓' : <span style={{color:'var(--text-3)'}}>—</span>}
                  </td>
                  <td style={{ textAlign:'center', padding:'12px 16px' }}>
                    {m.is_online ? '✓' : <span style={{color:'var(--text-3)'}}>—</span>}
                  </td>
                  <td style={{ textAlign:'center', padding:'12px 16px' }}>
                    {m.is_manual !== false ? '✓' : <span style={{color:'var(--text-3)'}}>—</span>}
                  </td>
                  <td style={{ textAlign:'center', padding:'12px 16px' }}>
                    {m.is_cash ? <span className="badge badge-amber">Espèces</span> : <span style={{color:'var(--text-3)'}}>—</span>}
                  </td>
                  <td style={{ textAlign:'center', padding:'12px 16px' }}>
                    <span className={`badge ${m.is_active ? 'badge-green' : 'badge-gray'}`}>
                      {m.is_active ? 'Actif' : 'Inactif'}
                    </span>
                  </td>
                  <td style={{ padding:'12px 20px', textAlign:'right' }}>
                    <div style={{ display:'flex', gap:6, justifyContent:'flex-end' }}>
                      <button className="btn btn-secondary btn-sm" onClick={() => setEditing(m)}>
                        <Edit2 size={13}/>
                      </button>
                      <button className="btn btn-secondary btn-sm" onClick={() => toggle(m)}
                        title={m.is_active ? 'Désactiver' : 'Activer'}>
                        {m.is_active ? <EyeOff size={13}/> : <Eye size={13}/>}
                      </button>
                      <button className="btn btn-danger btn-sm" onClick={() => setConfirmRemove(m)}>
                        <Trash2 size={13}/>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {methods.length === 0 && (
                <tr><td colSpan={7} style={{ textAlign:'center', padding:30,
                  color:'var(--text-3)' }}>Aucun moyen de paiement</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {(showNew || editing) && (
        <MethodModal
          method={editing}
          onClose={() => { setEditing(null); setNew(false); }}
          onSaved={load}
        />
      )}
      {confirmRemove && (
        <ConfirmModal
          title="Supprimer le moyen de paiement"
          message={`Supprimer "${confirmRemove.name}" ?`}
          confirmLabel="Supprimer"
          danger
          loading={removing}
          onConfirm={remove}
          onCancel={() => setConfirmRemove(null)}
        />
      )}
      {mergeTarget && (
        <MergeModal
          method={mergeTarget}
          methods={methods}
          onClose={() => setMergeTarget(null)}
          onMerged={load}
        />
      )}
    </>
  );
}
